'use client';

import type { Session, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/database.types';
import { useRouter, usePathname } from 'next/navigation';
import { createContext, useContext, useEffect, useState, Suspense } from 'react';
import { isPublicBioRoute } from '@/lib/privacy/private-routes';
import { createClient } from '@/utils/supabase/client';
import { COOKIE_CONSENT_UPDATED_EVENT } from '@/lib/cookie-consent/events';
import { identifyTelemetryUser, resetTelemetryUser } from '@/lib/telemetry/client';
import { isRecoverableSupabaseAuthError } from '@/utils/supabase/auth-errors';

type SupabaseContext = {
  supabase: SupabaseClient<Database>;
};

const Context = createContext<SupabaseContext | undefined>(undefined);

export default function SupabaseProvider({ children }: { children: React.ReactNode }) {
  // Create auth only when a consumer or the route-aware lifecycle needs it.
  // The pathname may suspend during prerender; page content must not wait for it.
  const [context] = useState(() => {
    let client: SupabaseClient<Database> | undefined;
    return {
      get supabase() {
        return (client ??= createClient());
      },
    };
  });
  return (
    <Context.Provider value={context}>
      <Suspense fallback={null}>
        <AuthLifecycle />
      </Suspense>
      {children}
    </Context.Provider>
  );
}

function AuthLifecycle() {
  const pathname = usePathname();
  const publicBio = isPublicBioRoute(pathname);
  const context = useSupabase();
  const [supabase, setSupabase] = useState(() => (publicBio ? undefined : context.supabase));
  // Once auth starts, keep listening through navigation to a public bio.
  if (!publicBio && !supabase) setSupabase(context.supabase);
  const router = useRouter();

  useEffect(() => {
    if (!supabase) return;
    const handleRecoverableAuthError = async (error: unknown) => {
      if (!isRecoverableSupabaseAuthError(error as { message?: string; code?: string })) {
        console.error('[SupabaseProvider] Auth session read failed:', error);
        return;
      }

      resetTelemetryUser();
      await supabase.auth.signOut({ scope: 'local' });
    };

    const syncPostHogIdentityFromSession = (session: Session | null) => {
      if (!session?.user) {
        return;
      }

      identifyTelemetryUser(session.user.id, document.cookie);
    };

    const syncPostHogIdentity = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        await handleRecoverableAuthError(error);
        return;
      }

      syncPostHogIdentityFromSession(data.session);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      try {
        if (event === 'SIGNED_IN' && session?.user) {
          syncPostHogIdentityFromSession(session);
          router.refresh();
        } else if (event === 'SIGNED_OUT') {
          // Clear user info when signing out
          resetTelemetryUser();
          // Refresh so server components re-render against the cleared session;
          // without this the UI keeps showing the signed-in state until a manual
          // reload. Symmetric with the SIGNED_IN refresh above.
          router.refresh();
        }
      } catch (error) {
        // Supabase invokes auth callbacks while holding an exclusive auth lock.
        // Defer recovery so any further auth call runs after this callback returns.
        setTimeout(() => {
          void handleRecoverableAuthError(error);
        }, 0);
      }
    });

    // Also check for existing session on mount
    void supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          void handleRecoverableAuthError(error);
          return;
        }
        if (!session?.user) {
          resetTelemetryUser();
        }
        syncPostHogIdentityFromSession(session);
      })
      .catch((error) => {
        void handleRecoverableAuthError(error);
      });

    const handleConsentUpdated = () => {
      void syncPostHogIdentity();
    };

    window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleConsentUpdated);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleConsentUpdated);
    };
  }, [router, supabase]);

  return null;
}

export const useSupabase = () => {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error('useSupabase must be used inside SupabaseProvider');
  }

  return context;
};
